import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import Table from './scoreTable';
import TextInput from 'components/customComponents/Experiment/ImagingSchedule/TextInput';
import { RMColorPicker } from '../../GlobalPrefrences/GlobalPrefrences';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import { convertRGBToHex } from 'utils/Global';
import { EntityStateDTO, ScoreDefinitionDTO } from 'business/models/Models';
import { generalSetupContext } from '../SetupDashboard';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import './setup.css';

interface Props {
  scoreGroupId: number;
  selectedRowIndex: number;
  setSelectedRowIndex(val: number): void;
  setSelectedListIndex(val: number): void;
  isNameDuplicate(index: number, value: string): boolean;
  getScoreListIndex(index: number): number;
}

export const ScoreSettings = memo((props: Props) => {
  const {
    scoreGroupId,
    selectedRowIndex,
    setSelectedRowIndex,
    setSelectedListIndex,
    isNameDuplicate,
    getScoreListIndex,
  } = props;

  const { scoresList, setScoresList, scoresGroupsList } = useContext(generalSetupContext);
  const [values, setValues] = useState<
    {
      selector: JSX.Element;
      scoreName: JSX.Element;
      orderNumber: JSX.Element;
      value: JSX.Element;
      hotKey: JSX.Element;
      color: JSX.Element;
      hit: JSX.Element;
      isoValue: JSX.Element;
    }[]
  >([]);
  const [filteredScoresList, setfilteredScoresList] = useState<ScoreDefinitionDTO[]>([]);

  useEffect(() => {
    if (scoreGroupId === undefined) {
      return;
    }
    const scoreList = scoresList.filter(
      (value) => value.wellDropScoreGroupId == scoreGroupId,
    );
    setfilteredScoresList(scoreList);
  }, [scoreGroupId, scoresList]);

  const setupScoreTable = useCallback(() => {
    const tableData = filteredScoresList.map((score, index) => {
      const entry = {
        selector: (
          <RMCheckBoxY isChecked={selectedRowIndex === index} onChange={() => {}} />
        ),
        scoreName: (
          <TextInput
            type="text"
            value={score.scoreName}
            id={`scoresettings-table-score-${score.scoreName}`}
            rowData={score}
            onChange={onChangeScoreName}
            index={index}
            style={{ flex: '1 1 27%' }}
          />
        ),
        orderNumber: (
          <>
            <div
              id={`scoresettings-setupscoretable-move-up-${index}`}
              data-testid={`scoresettings-setupscoretable-move-up-${index}`}
              className="btn p-1"
              onClick={(e) => {
                moveUpward(index, e);
              }}
            >
              <RMSvgIcon
                id="scoresettings-setupscoretable-svg-setup-ascending-icon"
                name="Ascending"
                width={'20px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
            <div
              id={`scoresettings-setupscoretable-move-down-${index}`}
              data-testid={`scoresettings-setupscoretable-move-down-${index}`}
              className="btn p-1"
              onClick={(e) => moveDownward(index, e)}
            >
              <RMSvgIcon
                id="scoresettings-setupscoretable-svg-setup-descending-icon"
                name="Descending"
                width={'20px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
          </>
        ),
        value: (
          <TextInput
            type="text"
            value={score.value}
            id={`scoresettings-table-value-${score.value}`}
            rowData={score}
            onChange={onChangeValue}
            index={index}
          />
        ),
        hotKey: (
          <TextInput
            type="text"
            value={score.hotKey}
            id={`scoresettings-table-hotkey-${score.hotKey}`}
            rowData={score}
            onChange={onChangeHotKey}
            index={index}
          />
        ),
        color: (
          <RMColorPicker
            key={score.wellDropScoreId}
            color={convertRGBToHex(score.color)}
            onChange={(e: any) => onChangeColor(e, index)}
            showChangeButton={false}
            index={index}
          />
        ),
        hit: (
          <span id="scoresettings-table-hit" onClick={(e) => e.stopPropagation()}>
            <RMCheckBoxY
              isChecked={score.hit}
              onChange={() => onChangeHit(index, !score.hit)}
            />
          </span>
        ),
        isoValue: (
          <TextInput
            type="text"
            value={score.isoValue ?? ''}
            id={`scoresettings-table-isovalue-${index}`}
            rowData={score}
            onChange={onChangeIsoValue}
            index={index}
          />
        ),
      };
      return entry;
    });
    setValues(tableData);
  }, [filteredScoresList, scoresList, selectedRowIndex]);

  const moveUpward = useCallback(
    (index: number, e) => {
      e.stopPropagation(); // To avoid row selection on clicking move up button.
      const newList = [...scoresList];
      if (!filteredScoresList[index - 1]) {
        return;
      }

      const listCurrentIndex = getScoreListIndex(index);
      const listPrevIndex = getScoreListIndex(index - 1);

      const upperOrderNumber = newList[listPrevIndex].orderNumber;
      newList[listPrevIndex].orderNumber = newList[listCurrentIndex].orderNumber;
      newList[listCurrentIndex].orderNumber = upperOrderNumber;

      const upperRow = newList[listPrevIndex];
      newList[listPrevIndex] = newList[listCurrentIndex];
      newList[listCurrentIndex] = upperRow;

      newList[listPrevIndex].entityState = EntityStateDTO.Modified;
      newList[listCurrentIndex].entityState = EntityStateDTO.Modified;

      setScoresList(newList);
    },
    [scoresList, filteredScoresList, getScoreListIndex],
  );

  const moveDownward = useCallback(
    (index: number, e) => {
      e.stopPropagation(); // To avoid row selection on clicking move down button.
      const newList = [...scoresList];
      if (!filteredScoresList[index + 1]) {
        return;
      }
      const listCurrentIndex = getScoreListIndex(index);
      const listNextIndex = getScoreListIndex(index + 1);

      const lowerOrderNumber = newList[listNextIndex].orderNumber;
      newList[listNextIndex].orderNumber = newList[listCurrentIndex].orderNumber;
      newList[listCurrentIndex].orderNumber = lowerOrderNumber;

      const lowerRow = newList[listNextIndex];
      newList[listNextIndex] = newList[listCurrentIndex];
      newList[listCurrentIndex] = lowerRow;

      newList[listNextIndex].entityState = EntityStateDTO.Modified;
      newList[listCurrentIndex].entityState = EntityStateDTO.Modified;
      setScoresList(newList);
    },
    [scoresList, filteredScoresList, getScoreListIndex],
  );

  const scoretable = useCallback(() => {
    const columns = getTableColumns();
    if (columns !== undefined && values !== null) {
      return (
        <Table
          columns={columns}
          data={values}
          onRowClick={onSelectRow}
          selectedRowIndex={selectedRowIndex}
        />
      );
    }
    return null;
  }, [values]);

  useEffect(() => {
    setupScoreTable();
  }, [filteredScoresList, selectedRowIndex, scoresList]);

  const getTableColumns = () => {
    let hidden = '';
    if (scoresGroupsList.length > 0) {
      const tempScoreList = scoresGroupsList.find(
        (scoregroup) => scoregroup.scoreGroupsId === scoreGroupId,
      );
      if (tempScoreList?.isAutoScore === true) {
        hidden = 'hidden';
      }
    }
    const columns = [
      {
        Header: '',
        accessor: 'selector',
      },
      {
        Header: (
          <>
            <span id="scoresettings-tablecolums-scoreame-label" className="float-left">
              Score Name
            </span>
            <span
              id="scoresettings-tablecolums-scorename-chevron-container"
              className="float-right score-name-chevron-container"
            >
              <RMSvgIcon
                id="scoresettings-tablecolums-scoreName-icon-asc"
                name="Ascending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.scoreName > b.scoreName ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
              <RMSvgIcon
                id="scoresettings-tablecolums-scoreName-icon-desc"
                name="Descending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.scoreName < b.scoreName ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
            </span>
          </>
        ),
        accessor: 'scoreName',
        className: 'input-score-name ',
      },
      {
        Header: (
          <>
            <span id="scoresettings-tablecolums-value-label" className="float-left">
              Value
            </span>
            <span
              id="scoresettings-tablecolums-value-chevron-container"
              className="float-right score-column-chevron-container"
            >
              <RMSvgIcon
                id="scoresettings-tablecolums-value-icon-asc"
                name="Ascending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.value > b.value ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
              <RMSvgIcon
                id="scoresettings-tablecolums-value-icon-desc"
                name="Descending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.value < b.value ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
            </span>
          </>
        ),
        accessor: 'value',
      },
      {
        Header: (
          <>
            <span id="scoresettings-tablecolums-hotkey-label" className="float-left">
              Hot Key
            </span>
            <span
              id="scoresettings-tablecolums-hotkey-chevron-container"
              className="float-right score-column-chevron-container"
            >
              <RMSvgIcon
                id="scoresettings-tablecolums-hotkey-icon-asc"
                name="Ascending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.hotKey > b.hotKey ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
              <RMSvgIcon
                id="scoresettings-tablecolums-hotkey-icon-desc"
                name="Descending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.hotKey < b.hotKey ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
            </span>
          </>
        ),
        accessor: 'hotKey',
        className: 'hotkey ' + hidden,
      },
      {
        Header: <span id="scoresettings-tablecolums-color-label">Color</span>,
        accessor: 'color',
        className: 'color',
      },
      {
        Header: <span id="scoresettings-tablecolums-hit-label">hit</span>,
        accessor: 'hit',
        className: 'hit',
      },
      {
        Header: (
          <>
            <span id="scoresettings-tablecolums-iso%-label" className="float-left">
              ISO%
            </span>
            <span
              id="scoresettings-tablecolums-iso%-chevron-container"
              className="float-right score-column-chevron-container"
            >
              <RMSvgIcon
                id="scoresettings-tablecolums-iso%-icon-asc"
                name="Ascending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.isoValue > b.isoValue ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
              <RMSvgIcon
                id="scoresettings-tablecolums-iso%-icon-desc"
                name="Descending"
                width={'12px'}
                height={'10px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {
                  const tempList = scoresList.sort((a, b) =>
                    a.isoValue < b.isoValue ? 1 : -1,
                  );
                  setScoresList(tempList);
                }}
              ></RMSvgIcon>
            </span>
          </>
        ),
        accessor: 'isoValue',
        className: 'iso ' + hidden,
      },
      {
        Header: <span id="scoresettings-tablecolums-order-label">Order</span>,
        accessor: 'orderNumber',
        className: 'order',
      },
    ];
    return columns;
  };

  const onChangeScoreName = useCallback(
    ({ value, index }): void => {
      isNameDuplicate(index, value);
      const newList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      newList[listIndex].scoreName = value;

      if (newList[listIndex].entityState !== EntityStateDTO.New) {
        newList[listIndex].entityState = EntityStateDTO.Modified;
      }
      setScoresList(newList);
    },
    [filteredScoresList],
  );

  const onChangeValue = useCallback(
    ({ value, index }): void => {
      const newList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      newList[listIndex].value = value;
      if (newList[listIndex].entityState !== EntityStateDTO.New) {
        newList[listIndex].entityState = EntityStateDTO.Modified;
      }
      setScoresList(newList);
    },
    [filteredScoresList],
  );

  const onChangeHotKey = useCallback(
    ({ value, index }): void => {
      if (isHotKeyDuplicate(index, value)) {
        return;
      }
      const newList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      newList[listIndex].hotKey = value;
      if (newList[listIndex].entityState !== EntityStateDTO.New) {
        newList[listIndex].entityState = EntityStateDTO.Modified;
      }
      setScoresList(newList);
    },
    [filteredScoresList],
  );

  const isHotKeyDuplicate = useCallback(
    (index: number, value: string): boolean => {
      const isDuplicateValue = false; //tempList.some((item, itemIndex) => item.hotKey === value && itemIndex !== listIndex && item.wellDropScoreGroupId == scoreGroupId);
      if (isDuplicateValue) {
        alert('Please provide a Hot Key.');
      }
      return isDuplicateValue;
    },
    [filteredScoresList],
  );

  const onChangeIsoValue = useCallback(
    ({ value, index }): void => {
      const newList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      newList[listIndex].isoValue = +value;
      if (newList[listIndex].entityState !== EntityStateDTO.New) {
        newList[listIndex].entityState = EntityStateDTO.Modified;
      }
      setScoresList(newList);
    },
    [filteredScoresList],
  );

  const onChangeColor = useCallback(
    (value: string, index: number): void => {
      const newList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      newList[listIndex].color = value;
      if (newList[listIndex].entityState !== EntityStateDTO.New) {
        newList[listIndex].entityState = EntityStateDTO.Modified;
      }
      setScoresList(newList);
    },
    [filteredScoresList],
  );

  const onChangeHit = useCallback(
    (index: number, value: boolean): void => {
      const newList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      newList[listIndex].hit = value;
      if (newList[listIndex].entityState !== EntityStateDTO.New) {
        newList[listIndex].entityState = EntityStateDTO.Modified;
      }
      setScoresList(newList);
    },
    [filteredScoresList],
  );

  const onSelectRow = useCallback(
    (row: { index: number }): void => {
      setSelectedRowIndex(row.index);
      setSelectedListIndex(getScoreListIndex(row.index));
    },
    [filteredScoresList],
  );

  return (
    <>
      <div
        id={`scoresettings-table`}
        className="setup-score-table-height mt-1 setup-score-table"
      >
        {scoretable()}
      </div>
    </>
  );
});
